import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { faGoogle, faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { TranslateService } from '@ngx-translate/core';

import { CalendarService } from '../services/calendar.service';
import { CalendarStatusModel } from '../models/calendar-status.model';

@Component({
  selector: 'app-calendar-settings-modal',
  templateUrl: './calendar-settings-modal.component.html',
  styleUrls: ['./calendar-settings-modal.component.scss'],
})
export class CalendarSettingsModalComponent implements OnInit, OnDestroy {
  public calendarStatus: CalendarStatusModel = {
    googleCalendarConnected: false,
    microsoftCalendarConnected: false,
  };
  public isLoading: boolean = true;
  public preferredMeetingProvider: string = '';
  public isUpdatingPreference: boolean = false;
  private destroyed$: Subject<boolean> = new Subject<boolean>();

  // Font Awesome icons
  public faGoogle = faGoogle;
  public faMicrosoft = faMicrosoft;

  constructor(
    private calendarService: CalendarService,
    private dialogRef: MatDialogRef<CalendarSettingsModalComponent>,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.calendarService.preferredMeetingProvider$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(provider => {
        this.preferredMeetingProvider = provider || '';
    });

    this.loadCalendarStatus();
    this.checkForCalendarCallback();
  }

  /**
   * Both calendars connected — show meeting provider preference
   */
  public get bothCalendarsConnected(): boolean {
    return this.calendarStatus.googleCalendarConnected
      && this.calendarStatus.microsoftCalendarConnected;
  }

  /**
   * Load calendar connection status from backend
   */
  private loadCalendarStatus(): void {
    this.isLoading = true;
    this.calendarService
      .getCalendarStatus()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (status: CalendarStatusModel) => {
          this.calendarStatus = status;
          this.calendarService.updateMeetingProvider(status.preferredMeetingProvider || null);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading calendar status:', error);
          this.showMessage(
            this.translateService.instant('client.calendarSettings.loadError') || 'Failed to load calendar status',
            'error-snackbar'
          );
          this.isLoading = false;
        },
      });
  }

  /**
   * Check if returning from OAuth callback
   */
  private checkForCalendarCallback(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroyed$))
      .subscribe((params) => {
        const calendarParam = params['calendar'];
        if (calendarParam === 'google-connected') {
          this.showMessage(
            this.translateService.instant('client.calendarSettings.googleConnected') || 'Google Calendar connected successfully!',
            'success-snackbar'
          );
          this.cleanUrlParams();
          this.loadCalendarStatus();
        } else if (calendarParam === 'microsoft-connected') {
          this.showMessage(
            this.translateService.instant('client.calendarSettings.microsoftConnected') || 'Microsoft Calendar connected successfully!',
            'success-snackbar'
          );
          this.cleanUrlParams();
          this.loadCalendarStatus();
        }
      });
  }

  /**
   * Clean URL parameters after OAuth callback
   */
  private cleanUrlParams(): void {
    const currentUrl = this.router.url.split('?')[0];
    this.router.navigate([currentUrl], { replaceUrl: true });
  }

  /**
   * Connect Google Calendar - AJAX call to get OAuth URL, then redirect
   */
  public connectGoogleCalendar(): void {
    this.calendarService
      .initiateGoogleConnect()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          localStorage.setItem('calendarReturnUrl', this.router.url);
          window.location.href = response.authUrl;
        },
        error: (error) => {
          console.error('Error initiating Google Calendar connect:', error);
          this.showMessage('Failed to initiate Google Calendar connection', 'error-snackbar');
        },
      });
  }

  /**
   * Connect Microsoft Calendar - AJAX call to get OAuth URL, then redirect
   */
  public connectMicrosoftCalendar(): void {
    this.calendarService
      .initiateMicrosoftConnect()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          localStorage.setItem('calendarReturnUrl', this.router.url);
          window.location.href = response.authUrl;
        },
        error: (error) => {
          console.error('Error initiating Microsoft Calendar connect:', error);
          this.showMessage('Failed to initiate Microsoft Calendar connection', 'error-snackbar');
        },
      });
  }

  /**
   * Disconnect Google Calendar
   */
  public disconnectGoogleCalendar(): void {
    if (!confirm('Are you sure you want to disconnect Google Calendar?')) {
      return;
    }

    this.calendarService
      .disconnectGoogleCalendar()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.showMessage('Google Calendar disconnected', 'success-snackbar');
          this.loadCalendarStatus();
        },
        error: (error) => {
          console.error('Error disconnecting Google Calendar:', error);
          this.showMessage('Failed to disconnect Google Calendar', 'error-snackbar');
        },
      });
  }

  /**
   * Disconnect Microsoft Calendar
   */
  public disconnectMicrosoftCalendar(): void {
    if (!confirm('Are you sure you want to disconnect Microsoft Calendar?')) {
      return;
    }

    this.calendarService
      .disconnectMicrosoftCalendar()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.showMessage('Microsoft Calendar disconnected', 'success-snackbar');
          this.loadCalendarStatus();
        },
        error: (error) => {
          console.error('Error disconnecting Microsoft Calendar:', error);
          this.showMessage('Failed to disconnect Microsoft Calendar', 'error-snackbar');
        },
      });
  }

  /**
   * Handle meeting provider preference change
   */
  public onMeetingProviderChange(provider: string): void {
    this.isUpdatingPreference = true;
    this.calendarService
      .setMeetingPreference(provider)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.isUpdatingPreference = false;
          this.showMessage(
            this.translateService.instant('client.calendarSettings.preferenceUpdated'),
            'success-snackbar'
          );
        },
        error: (error) => {
          console.error('Error updating meeting preference:', error);
          this.isUpdatingPreference = false;
          this.showMessage(
            this.translateService.instant('client.calendarSettings.preferenceUpdateError'),
            'error-snackbar'
          );
        },
      });
  }

  /**
   * Show snackbar message
   */
  private showMessage(message: string, panelClass: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: [panelClass],
    });
  }

  /**
   * Close modal
   */
  public close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
